import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import {
  Loader2, FolderOpen, ExternalLink, Download, FileText, Youtube, Link2,
  Image as ImageIcon, Film, File as FileIcon, CalendarClock, CheckCircle2, Clock, Circle,
} from "lucide-react";

const ytId = (url) => {
  const m = (url || "").match(/(?:youtu\.be\/|v=|embed\/)([\w-]{11})/);
  return m ? m[1] : null;
};
const iconFor = (r) => {
  if (r.kind === "link") return (r.url || "").match(/youtu\.?be/) ? Youtube : Link2;
  const ct = r.content_type || "";
  if (ct.startsWith("image/")) return ImageIcon;
  if (ct.startsWith("video/")) return Film;
  if (ct.includes("pdf")) return FileText;
  return FileIcon;
};

/* ---------------- Recursos públicos del docente ---------------- */
export function StudentResourcesPanel({ token }) {
  const H = { "X-Parent-Token": token };
  const [items, setItems] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [subject, setSubject] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/student/teacher-resources", { headers: H })
      .then(({ data }) => { setItems(data.resources || []); setSubjects(data.subjects || []); })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [token]);

  const filtered = useMemo(() => (subject ? items.filter((r) => r.subject === subject) : items), [items, subject]);

  const open = (r) => {
    if (r.kind === "link") { window.open(r.url, "_blank", "noopener"); return; }
    window.open(`${API}${r.file_url}?auth=${encodeURIComponent(token)}`, "_blank", "noopener");
  };

  return (
    <div data-testid="student-recursos-panel">
      <div className="flex items-center gap-2 mb-4">
        <FolderOpen className="w-5 h-5 text-navy" />
        <h2 className="font-display font-bold text-lg text-navy">Recursos de mis maestros</h2>
        {subjects.length > 0 && (
          <select value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="student-res-subject" className="ml-auto text-xs border border-nardo-200 rounded-lg px-2.5 py-1.5 text-navy outline-none focus:border-navy">
            <option value="">Todas las materias</option>
            {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
      </div>
      {loading ? (
        <div className="py-12 text-center text-nardo-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-nardo-200 py-12 text-center text-nardo-500 text-sm">
          <FolderOpen className="w-10 h-10 mx-auto mb-3 text-nardo-300" />
          Tus maestros aún no han compartido recursos.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((r) => {
            const Icon = iconFor(r);
            const ct = r.content_type || "";
            const isImg = r.kind === "file" && ct.startsWith("image/");
            const isPdf = r.kind === "file" && ct.includes("pdf");
            const yt = r.kind === "link" ? ytId(r.url) : null;
            const fileHref = r.file_url ? `${API}${r.file_url}?auth=${encodeURIComponent(token)}` : null;
            return (
              <div key={r.id} data-testid={`student-res-${r.id}`} className="bg-white rounded-2xl border border-nardo-200 shadow-sm p-4 flex flex-col">
                {(isImg || isPdf || yt) && (
                  <div className="mb-3 rounded-xl overflow-hidden bg-nardo-100 h-32 flex items-center justify-center cursor-pointer" onClick={() => open(r)}>
                    {isImg && <img src={fileHref} alt={r.title} className="w-full h-full object-cover" loading="lazy" />}
                    {yt && <img src={`https://img.youtube.com/vi/${yt}/mqdefault.jpg`} alt={r.title} className="w-full h-full object-cover" loading="lazy" />}
                    {isPdf && <iframe src={`${fileHref}#toolbar=0&navpanes=0&view=FitH`} title={r.title} className="w-full h-full pointer-events-none" />}
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-navy/10 text-navy flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-navy text-sm leading-tight truncate">{r.title}</div>
                    <div className="text-[11px] text-nardo-400 truncate">{r.docente_name}</div>
                  </div>
                </div>
                {r.description && <p className="text-xs text-nardo-600 mt-2 line-clamp-2">{r.description}</p>}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {r.subject && <span className="text-[10px] px-2 py-0.5 rounded-full bg-navy/10 text-navy font-medium">{r.subject}</span>}
                </div>
                <button onClick={() => open(r)} data-testid={`student-res-open-${r.id}`} className="mt-3 text-xs inline-flex items-center justify-center gap-1.5 border border-navy text-navy hover:bg-navy hover:text-white px-3 py-1.5 rounded-lg font-semibold transition-colors">
                  {r.kind === "link" ? <ExternalLink className="w-3.5 h-3.5" /> : <Download className="w-3.5 h-3.5" />}
                  {r.kind === "link" ? "Abrir" : "Ver / descargar"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------------- Temario del ciclo (dosificación) ---------------- */
const ST = {
  completado: { label: "Visto", cls: "text-emerald-600", icon: CheckCircle2 },
  en_curso: { label: "En curso", cls: "text-amber-600", icon: Clock },
  pendiente: { label: "Próximamente", cls: "text-nardo-400", icon: Circle },
};

export function StudentTemarioPanel({ token }) {
  const H = { "X-Parent-Token": token };
  const [subjects, setSubjects] = useState([]);
  const [groupLabel, setGroupLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get("/student/dosificacion", { headers: H })
      .then(({ data }) => { setSubjects(data.subjects || []); setGroupLabel(data.group_label || ""); if (data.subjects?.length) setOpen(data.subjects[0].subject); })
      .catch(() => setSubjects([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [token]);

  return (
    <div data-testid="student-temario-panel">
      <div className="flex items-center gap-2 mb-4">
        <CalendarClock className="w-5 h-5 text-navy" />
        <h2 className="font-display font-bold text-lg text-navy">Temario del ciclo {groupLabel ? `· ${groupLabel}` : ""}</h2>
      </div>
      {loading ? (
        <div className="py-12 text-center text-nardo-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando…</div>
      ) : subjects.length === 0 ? (
        <div className="bg-white rounded-2xl border border-nardo-200 py-12 text-center text-nardo-500 text-sm">
          <CalendarClock className="w-10 h-10 mx-auto mb-3 text-nardo-300" />
          Tus maestros aún no publican el temario.
        </div>
      ) : (
        <div className="space-y-3">
          {subjects.map((s) => (
            <div key={s.subject} className="bg-white rounded-2xl border border-nardo-200 shadow-sm overflow-hidden" data-testid={`student-temario-${s.subject}`}>
              <button onClick={() => setOpen(open === s.subject ? null : s.subject)} className="w-full flex items-center gap-3 px-4 py-3 text-left">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-navy text-sm truncate">{s.subject}</div>
                  <div className="text-[11px] text-nardo-400">{s.docente_name || "Docente"} · {s.done}/{s.total} temas</div>
                </div>
                <div className="w-24 shrink-0">
                  <div className="text-[10px] text-right text-nardo-500 mb-0.5">{s.progress}%</div>
                  <div className="h-1.5 rounded-full bg-nardo-200 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${s.progress}%` }} /></div>
                </div>
              </button>
              {open === s.subject && (
                <div className="border-t border-nardo-100 divide-y divide-nardo-100">
                  {s.items.map((it, i) => {
                    const st = ST[it.status] || ST.pendiente;
                    const StIcon = st.icon;
                    return (
                      <div key={i} className="px-4 py-2.5 flex items-start gap-3">
                        <StIcon className={`w-4 h-4 mt-0.5 shrink-0 ${st.cls}`} />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-navy font-medium">{it.period ? `${it.period} · ` : ""}{it.tema || "—"}</div>
                          {it.contenido && <div className="text-xs text-nardo-500 mt-0.5">{it.contenido}</div>}
                        </div>
                        <span className={`text-[10px] font-semibold shrink-0 ${st.cls}`}>{st.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
